import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';

import { policiesQuery } from '@amfam/policy/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService } from '@amfam/shared/utility/shared-services';
import { DsModalService } from '@amfam/ui-kit';

import { AnalyticsEventType } from '../+state/quick-quote-feature/quick-quote-enum';
import { QuickQuoteFeatureFacade } from '../+state/quick-quote-feature/quick-quote-feature.facade';

export interface Expectation {
  text: string;
  showFindVinButton?: boolean;
}

export interface ExpectationContent {
  headerText: string;
  sendToAgentText: string;
}

export interface VinContent {
  linkText: string;
  headerText: string;
  definition: string;
  documentsSubHeader: string;
  vinOnDocument: string;
  yourVehicleSubHeaderText: string;
  vinOnVehicle: string;
}

@Component({
  selector: 'replace-vehicle-expectation-setting',
  templateUrl: './replace-vehicle-expectation-setting.component.html',
  styleUrls: ['./replace-vehicle-expectation-setting.component.scss'],
  standalone: false
})
export class ReplaceVehicleExpectationSettingComponent implements OnInit {
  @Output() exitEvent: EventEmitter<any> = new EventEmitter();
  @Output() continueEvent: EventEmitter<any> = new EventEmitter();
  @Output() sendToAgentEvent: EventEmitter<any> = new EventEmitter();

  expectations: Expectation[];
  expectationContent: ExpectationContent;
  vinContent: VinContent;
  titleHolders$: Observable<string>;
  isAuthorizedSendToAgent$: Observable<boolean> = this.roleService.isAuthorized(
    'replacevehiclequote_sendtoagent'
  );
  plural = false;

  findVinFlowModalId = this.modalService.createModalId('findVinFlowModalId');

  constructor(
    private copyService: CopyService,
    private store: Store<any>,
    private modalService: DsModalService,
    private roleService: ImpersonateRolesService,
    private quickQuoteFeatureFacade: QuickQuoteFeatureFacade,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngOnInit(): void {
    this.expectations = this.copyService.getCopy('policy.addVehicleExpectations');
    this.expectationContent = this.copyService.getCopy('policy.replaceVehicleExpectationsContent');
    this.vinContent = this.copyService.getCopy('policy.findVIN');
    this.getTitleHolders();
  }

  getTitleHolders() {
    this.titleHolders$ = this.store.select(policiesQuery.selectRolesforAdvanceAutoPolicy).pipe(
      filter(policyRoles => !!policyRoles && policyRoles.length > 0),
      first(),
      map(policyRoles => policyRoles[0].roles),
      map(roles => {
        const pni = roles.find(role => role.type.toLowerCase() === 'primarynamedinsured');
        const sni = roles.find(role => role.type.toLowerCase() === 'secondarynamedinsured');
        this.plural = !!sni;
        return `${pni.firstName} ${pni.lastName}  ${
          sni ? ' or ' + sni.firstName + ' ' + sni.lastName : ''
        } ${sni ? 'are ' : 'is '}`;
      })
    );
  }

  goBack() {
    this.store.dispatch(fromRouterActions.Back());
  }

  sendToMyAgent() {
    this.analyticsFacade.trackEvent({
      event: 'agent_referral',
      form_name: 'Replace Vehicle'
    });
    this.sendToAgentEvent.emit(true);
  }

  cancel() {
    this.exitEvent.emit(true);
  }

  findVIN(e) {
    e.preventDefault();
    this.quickQuoteFeatureFacade.sendAnalytics(
      AnalyticsEventType.REPLACE_VEHICLE_QUOTE_OR_BIND_FIND_VIN
    );

    this.openModal().then(() => {
      this.quickQuoteFeatureFacade.sendAnalytics(AnalyticsEventType.REPLACE_VEHICLE_FIND_VIN);
    });
  }

  continue() {
    this.continueEvent.emit(true);
  }

  private openModal = async () => {
    this.modalService.open(this.findVinFlowModalId);
  };
}
